import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { DataPoint } from './DataModels';
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from '@serendie/ui';
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextField } from '@iconics/components';
import { TypeHelper } from '@iconics/core';

class EditDataPoint extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;

    private dataPointTypes = [
        { value: 1, label: this.labels.DataPoint_DataPointType_Realtime },
        { value: 2, label: this.labels.DataPoint_DataPointType_Historical },
    ];
    private dataPointDataTypes = [
        { value: 1, label: this.labels.DataPoint_DataPointDataType_Integer },
        { value: 2, label: this.labels.DataPoint_DataPointDataType_String },
        { value: 3, label: this.labels.DataPoint_DataPointDataType_Float },
        { value: 4, label: this.labels.DataPoint_DataPointDataType_Enum },
    ];

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        const localStorageProvider = appContext().localStorageProvider;

        this.state =
        {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            enabled: false,
            sortOrder: 0,
            dataPointType: this.dataPointTypes[0].value,
            dataPointTypeLabel: this.dataPointTypes[0].label,
            dataPointDataType: this.dataPointDataTypes[0].value,
            dataPointDataTypeLabel: this.dataPointDataTypes[0].label,
            dataCollectionDeviceId: '',
            dataPointName: '',
            validationRule: '',
            validationRangeMinimum: '',
            validationRangeMaximum: '',
            concurrencyToken: '',
            canDelete: localStorageProvider.hasRight('DATAMODEL.DataPoints.Delete'),
            canUpdate: localStorageProvider.hasRight('DATAMODEL.DataPoints.Update'),
            sections: [],
        };
    }

    private get nodeId(): number
    {
        return Number.parseInt(this.props.params.nodeId ?? '0');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_DataPoint_PageTitle;
        this.backUrl = `/admin/nodes/${this.nodeId}/edit`;
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        await this.loadDataPoint();
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, _: Readonly<IState>): Promise<void>
    {
        if (!prevProps.params.id && this.props.params.id)
        {
            await this.loadDataPoint();
        }
    }

    private async loadDataPoint(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (this.props.params.id === undefined)
            {
                return;
            }
            const dataPointData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.byNodeid.dataPoints.byId.get<DataPoint>({
                id: this.props.params.id,
                nodeId: this.nodeId,
                tenancy: appContext().tenantId,
                select: DataPoint,
            });
            await this.setStateAsync({
                name: dataPointData.Name,
                displayName: this.languageValuesHelper.fromString(dataPointData.DisplayName),
                enabled: TypeHelper.toBoolean(dataPointData.IsEnabled) ?? false,
                sortOrder: dataPointData.SortOrder,
                dataPointType: dataPointData.DataPointType,
                dataPointTypeLabel: this.dataPointTypes.find(x => x.value === dataPointData.DataPointType)?.label ?? this.dataPointTypes[0].label,
                dataPointDataTypeLabel: this.dataPointDataTypes.find(x => x.value === dataPointData.DataPointDataType)?.label ?? this.dataPointDataTypes[0].label,
                dataPointDataType: dataPointData.DataPointType,
                dataCollectionDeviceId: dataPointData.DataCollectionDeviceId,
                dataPointName: dataPointData.DataPointName,
                validationRule: dataPointData.ValidationRule,
                validationRangeMinimum: dataPointData.ValidationRangeMinimum,
                validationRangeMaximum: dataPointData.ValidationRangeMaximum,
                concurrencyToken: dataPointData['@odata.etag'],
            });
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }

        if (this.props.params.id)
        {
            await this.updateDataPoint();
        }
        else
        {
            await this.createDataPoint();
        }
    }

    private async updateDataPoint(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (this.props.params.id === undefined)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.dataPoints.byId.put<DataPoint>({
                id: this.props.params.id,
                nodeId: this.nodeId,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    DataPointType: this.state.dataPointType,
                    DataPointDataType: this.state.dataPointDataType,
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    SortOrder: this.state.sortOrder,
                    DataCollectionDeviceId: this.state.dataCollectionDeviceId,
                    DataPointName: this.state.dataPointName,
                    ValidationRule: this.state.validationRule,
                    ValidationRangeMinimum: this.state.validationRangeMinimum,
                    ValidationRangeMaximum: this.state.validationRangeMaximum,
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async createDataPoint(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.byNodeid.dataPoints.post<DataPoint>({
                nodeId: this.nodeId,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    DataPointType: this.state.dataPointType,
                    DataPointDataType: this.state.dataPointDataType,
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    SortOrder: this.state.sortOrder,
                    DataCollectionDeviceId: this.state.dataCollectionDeviceId,
                    DataPointName: this.state.dataPointName,
                    ValidationRule: this.state.validationRule,
                    ValidationRangeMinimum: this.state.validationRangeMinimum,
                    ValidationRangeMaximum: this.state.validationRangeMaximum
                }
            });
            this.props.navigate(this.backUrl);
        });
    }

    private handleDataPointTypeSelect(value: number): void 
    {
        this.setStateAsync({ dataPointType: value, dataPointTypeLabel: this.dataPointTypes.find(x => x.value === value)?.label ?? '' });
    }

    private handleDataPointDataTypeSelect(value: number): void 
    {
        this.setStateAsync({ dataPointDataType: value, dataPointDataTypeLabel: this.dataPointDataTypes.find(x => x.value === value)?.label ?? '' });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (!this.props.params.id)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.byNodeid.dataPoints.byId.delete({
                tenancy: appContext().tenantId,
                id: this.props.params.id,
                nodeId: this.nodeId,
            });
            this.props.navigate(this.backUrl);
        });
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer spacing="cards">
                        <GridItem>
                            <Card
                                title={labels.Platform_DataPoint_DetailsTitle}
                                subTitle={labels.Platform_DataPoint_DetailsSubTitle}
                                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                            >
                                <GridContainer>
                                    <GridItem>
                                        <TextField
                                            required
                                            label={labels.DataPoint_Name}
                                            placeholder={labels.DataPoint_Name}
                                            value={this.state.name}
                                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                            invalid={!!this.state.nameError}
                                            invalidMessage={this.state.nameError}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            label={labels.DataPoint_Enabled}
                                            checked={this.state.enabled}
                                            onCheckedChange={e => this.setState({ enabled: e.checked as boolean })}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <MultilingualTextField
                                            label={labels.DataPoint_DisplayName}
                                            placeholder={labels.DataPoint_DisplayName}
                                            value={this.state.displayName}
                                            onChange={value => this.setState({ displayName: value })}
                                            localisation={appContext().localisation}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <TextField
                                            label={labels.DataPoint_SortOrder}
                                            placeholder={labels.DataPoint_SortOrder}
                                            value={this.state.sortOrder}
                                            type="number"
                                            onChange={e => this.setState({ sortOrder: Number.parseInt(e.target.value) })}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <Select
                                            label={labels.DataPoint_DataPointType}
                                            placeholder={this.state.dataPointTypeLabel}
                                            items={this.dataPointTypes.map(x => ({ value: x.value.toString(), label: x.label }))}
                                            onSelect={e => this.handleDataPointTypeSelect(Number.parseInt(e.value))}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <Select
                                            label={labels.DataPoint_DataPointDataType}
                                            placeholder={this.state.dataPointDataTypeLabel}
                                            items={this.dataPointDataTypes.map(x => ({ value: x.value.toString(), label: x.label }))}
                                            onSelect={e => this.handleDataPointDataTypeSelect(Number.parseInt(e.value))}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                        <GridItem>
                            <Card
                                title={labels.Platform_DataPoint_ConnectivityTitle}
                                subTitle={labels.Platform_DataPoint_ConnectivitySubTitle}
                                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                            >
                                <GridContainer>
                                    <GridItem>
                                        <TextField
                                            label={labels.DataPoint_DataCollectionDeviceId}
                                            placeholder={labels.DataPoint_DataCollectionDeviceId}
                                            value={this.state.dataCollectionDeviceId}
                                            onChange={e => this.setState({ dataCollectionDeviceId: e.target.value })}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TextField
                                            label={labels.DataPoint_DataPointName}
                                            placeholder={labels.DataPoint_DataPointName}
                                            value={this.state.dataPointName}
                                            onChange={e => this.setState({ dataPointName: e.target.value })}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TextField
                                            label={labels.DataPoint_ValidationRule}
                                            placeholder={labels.DataPoint_ValidationRule}
                                            value={this.state.validationRule}
                                            onChange={e => this.setState({ validationRule: e.target.value })}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <TextField
                                            label={labels.DataPoint_ValidationRangeMinimum}
                                            placeholder={labels.DataPoint_ValidationRangeMinimum}
                                            value={this.state.validationRangeMinimum}
                                            onChange={e => this.setState({ validationRangeMinimum: e.target.value })}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <TextField
                                            label={labels.DataPoint_ValidationRangeMaximum}
                                            placeholder={labels.DataPoint_ValidationRangeMaximum}
                                            value={this.state.validationRangeMaximum}
                                            onChange={e => this.setState({ validationRangeMaximum: e.target.value })}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                    </GridContainer>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                this.props.params.id &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    onDelete={() => this.handleDelete()}
                                    dialogMessage={this.labels.Platform_DataPoint_ConfirmDeleteMsg}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(this.backUrl)}
                            >
                                {labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.state.canUpdate || !this.validator.isValid}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditDataPoint);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
    nodeId: string;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    enabled: boolean;
    sortOrder: number;
    dataPointType: number;
    dataPointTypeLabel: string;
    dataPointDataType: number;
    dataPointDataTypeLabel: string;
    dataCollectionDeviceId: string;
    dataPointName: string;
    validationRule: string;
    validationRangeMinimum: string;
    validationRangeMaximum: string;
    concurrencyToken: string;
    canDelete: boolean;
    canUpdate: boolean;
    sections: IPageNavSection[];
}
